import AppStandardButton from "../../components/UI/AppButtons";
import React, { useEffect, useState, useMemo } from "react";
import { t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { BookingApi } from "../../API/Apis/BookingApi";
import { StripeTransactionDetails } from "../../@types/Booking/stripeTransactionDetails";
import BookingGetters from "../../store/onlineBooking/getters";
import TagManager from "react-gtm-module";

// --- Helper to get search params ---
const useStripeTransactionDetails = (
  search: string
): StripeTransactionDetails => {
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  return useMemo(
    () => ({
      sessionId: searchParams.get("sessionId") || "",
      priceId: searchParams.get("priceId") || "",
      reservationId: searchParams.get("reservationId") || "",
      userId: searchParams.get("userId") || "",
      amount: searchParams.get("amount") || "",
      prefixNo: searchParams.get("prefixNo") || "",
    }),
    [searchParams]
  );
};
// --- Helper to get Tag Manager config ---
const getIsEnabled = (settings) => !!settings?.enableTagManager;
const getId = (settings) => settings?.tagManagerId || "";
const getEvent = (settings) => settings?.gtagManagerEvent || "";
const getSendTo = (settings) => settings?.gTagManagerSendTo || "";
const getCurrency = (settings) => settings?.gTagManagerCurrency || "";
const getValueEnabled = (settings) => !!settings?.enableGtagManagerValue;
const getTransactionIdEnabled = (settings) =>
  !!settings?.enableGTagManagerTranscationId;

const getTagManagerConfig = (settings) => {
  return {
    isEnabled: getIsEnabled(settings),
    id: getId(settings),
    event: getEvent(settings),
    sendTo: getSendTo(settings),
    currency: getCurrency(settings),
    valueEnabled: getValueEnabled(settings),
    transactionIdEnabled: getTransactionIdEnabled(settings),
  };
};

export function BookingConfirmPageStripe() {
  const navigate = useNavigate();
  const location = useLocation();
  const stripePageRedirectionUrl = useStripeTransactionDetails(location.search);
  const appSettings = BookingGetters.getAppSettings();
  const [tagManagerConfig, setTagManagerConfig] = useState({
    isEnabled: false,
    id: "",
    event: "",
    sendTo: "",
    currency: "",
    valueEnabled: false,
    transactionIdEnabled: false,
  });

  useEffect(() => {
    BookingApi.savePaymentForStripe(stripePageRedirectionUrl);
    navigate(location.pathname, { replace: true });
  }, [navigate, location.pathname, stripePageRedirectionUrl]);

  useEffect(() => {
    setTagManagerConfig(getTagManagerConfig(appSettings));
  }, [appSettings]);

  useEffect(() => {
    if (tagManagerConfig.isEnabled && tagManagerConfig.id) {
      TagManager.initialize({ gtmId: tagManagerConfig.id });
    }
  }, [tagManagerConfig.isEnabled, tagManagerConfig.id]);

  useEffect(() => {
    if (appSettings?.enableDataLayer) {
      TagManager.dataLayer({
        dataLayer: {
          event: tagManagerConfig.event,
          send_to: tagManagerConfig.sendTo,
          value: tagManagerConfig.valueEnabled
            ? stripePageRedirectionUrl.amount
            : null,
          currency: tagManagerConfig.currency,
          transaction_id: tagManagerConfig.transactionIdEnabled
            ? stripePageRedirectionUrl.prefixNo
            : null,
        },
      });
    }
  }, [
    appSettings?.enableDataLayer,
    tagManagerConfig,
    stripePageRedirectionUrl,
  ]);

  return <BookingConfirmPageStripeContent />;
}

function BookingConfirmPageStripeContent() {
  return (
    <section className="min-h-[80vh] box-border px-44 flex flex-col border-t-2 border-solid border-grey-300">
      <header className="flex flex-col items-center gap-4 py-8">
        <img src="/assets/images/success-icon@2x.png" className="h-36 w-36" />
        <h1 className="text-3xl font-bold text-[#2267ae] py-2">
          {t("Congratulations! Your Booking Is Completed")}
        </h1>
        <div className="flex">
          <AppStandardButton
            href="/embed"
            className="w-fit py-4 mx-8"
            variant="outlined"
            color="secondaryColor"
          >
            <span className="normal-case text-[#2267ae] p-1 text-lg">
              Homepage
            </span>
          </AppStandardButton>
          <AppStandardButton
            href="/embed"
            className="w-fit py-4 mx-8"
            color="secondaryColor"
            variant="outlined"
          >
            <span className="normal-case text-[#2267ae] p-1 text-lg">
              Dashboard
            </span>
          </AppStandardButton>
        </div>
      </header>
    </section>
  );
}
